import { Box, Slide, Typography, styled } from '@mui/material';
import BookTripUrgencyMessage from './BookUrgencyMessage';
import BookTripButton from './BookTripButton';
import { useIsVisible } from 'src/hooks/useIsVisible';
import { BookTripProps } from './BookTrip';
import BookNowPrice from './BookNowPrice';
import { useCallback, useEffect, useRef } from 'react';
import { useTrip } from 'src/hooks/useTrip';
import FlashpackButton from '../FlashpackButton/FlashpackButton';
import { trackAskAnExpertEvent } from 'src/utils/AnalyticsTracking';

const BookTripToolbar: React.FC<BookTripProps> = ({
  title,
  code,
  days,
  priceOverrides,
  bookingLinkOverride,
  bookingSystemOverride = false,
}) => {
  const ref = useRef<HTMLElement>(
    document.getElementById('toolbar-trigger') as unknown as HTMLElement,
  );
  useEffect(() => {
    if (!ref.current) {
      ref.current = document.getElementById('toolbar-trigger') as unknown as HTMLElement;
    }
  }, [ref]);
  const { data } = useTrip({ code });

  const bookNowBoxVisible = useIsVisible(ref, {
    top: '-200px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  });

  const handleExpertClick = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('show');
    } else {
      console.error('Intercom is not available');
    }
    trackAskAnExpertEvent(code);
  }, [code]);

  return (
    <Slide in={bookNowBoxVisible == false} direction="down">
      <BookNowToolbarContainer>
        <BookNowToolbar>
          <Typography sx={{ gridArea: 'title' }} variant="H4">
            {title}
          </Typography>
          <BookNowPrice
            days={days}
            priceOverrides={priceOverrides}
            priceFrom={data?.priceRange.min}
            sx={{
              justifySelf: 'right',
            }}
          />
          <BookTripButtonSection
            title={title}
            code={code}
            forceUnavailable={bookingSystemOverride || !data?.nextDepartureDate}
            override={bookingLinkOverride}
          ></BookTripButtonSection>
          <ExpertButtonSection onClick={handleExpertClick} variant="secondary">
            Ask an expert
          </ExpertButtonSection>
          <BookTripUrgencySection
            lastBookingAt={data?.lastBookingAt}
            numberOfRecentBookings={data?.numberOfRecentBookings}
          />
        </BookNowToolbar>
      </BookNowToolbarContainer>
    </Slide>
  );
};

const BookTripUrgencySection = styled(BookTripUrgencyMessage)(() => ({
  gridArea: 'urgency',
  justifySelf: 'right',
}));

const BookTripButtonSection = styled(BookTripButton)(() => ({
  gridArea: 'button',
  justifySelf: 'right',
  width: '100%',
}));

const ExpertButtonSection = styled(FlashpackButton)(({ theme }) => ({
  gridArea: 'expert',
  justifySelf: 'right',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    justifySelf: 'stretch',
  },
}));

const BookNowToolbar = styled(Box)(() => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  columnGap: '24px',
  paddingBlock: 12,
  gridTemplateAreas: '"title price button expert" "title urgency button expert"',
  maxWidth: 1176,
  marginInline: 'auto',
  gridTemplateRows: '1fr min-content',
}));

const BookNowToolbarContainer = styled(Box)(({ theme }) => ({
  top: 'var(--flashpack-book-trip-toolbar-top, 76px)',
  left: 0,
  right: 0,
  zIndex: 10,
  position: 'fixed',
  borderWidth: 0,
  borderBottomWidth: 1,
  borderRadius: 0,
  borderStyle: 'solid',
  borderColor: theme.palette.principal.grey50,
  backgroundColor: theme.palette.principal.white,
  [theme.breakpoints.down(1140)]: {
    paddingInline: 48,
  },
}));

export default BookTripToolbar;
